//system imports
import { useEffect, useState } from 'react';
import { plainToClass } from 'class-transformer';
//third party imports
import { Form } from 'antd';
import moment from 'moment';
//custom //user defined imports
import { useMessenger } from 'tools/view-hooks/messenger-hook';
import { AddContractController, SelectorOption } from 'fragments/add-contract/interfaces';
import { PropertiesService, useAPIPropertieService } from 'services/properties/properties.service';
import { ContractDto } from 'services/contracts/contract.dto';
import { ContractsService, useAPIContractService } from 'services/contracts/contracts.service';
import { PersonsService, useAPIPersonsService } from 'services/persons/persons.service';
import { CompletePropertyDto } from 'services/properties/property-complete.dto';
import { CompletePersonDto } from 'services/persons/person-complete.dto';
import { CompleteContractDto } from 'services/contracts/contract-complete.dto';
import { useAPISettingsService } from 'services/settings/settings.service';
import { ProvinceDto } from 'services/settings/settings.dto';
import { InformationInterestModal } from 'fragments/contract-card/modal-information-interest/interfaces';
import { ExtractedContractDto } from 'services/contracts/extracted-contract.dto';

export const useAddContractController = (
    onContractAdded: (dto: CompleteContractDto) => void,
    extractedData?: ExtractedContractDto,
    propertiesService: PropertiesService = useAPIPropertieService(),
    personsService: PersonsService = useAPIPersonsService(),
    settingsService = useAPISettingsService(),
    contractsService: ContractsService = useAPIContractService(),
    messenger = useMessenger(),
): AddContractController => {
    const [isLoading, setIsLoading] = useState(false);
    const [selectIsOpen, setSelectIsOpen] = useState(false);
    const [formInstance] = Form.useForm();
    const [propertiesOptions, setPropertiesOptions] = useState<SelectorOption[]>([]);
    const [personsOptions, setPersonsOptions] = useState<SelectorOption[]>([]);
    const [isFileLoading, setIsFileLoading] = useState<boolean>(false);
    const [fileUrl, setFileUrl] = useState<string>('');
    const [fileId, setFileId] = useState(extractedData?.fileId ?? 0);
    const [duration, setDuration] = useState(extractedData?.contract.durationMonths ?? 0);
    const [initialDate, setInitialDate] = useState<any>(
        extractedData?.contract.initialDate ? moment(extractedData.contract.initialDate) : undefined,
    );
    const [finishDate, setFinishDate] = useState<any>();
    const [provincesOptions, setProvincesOptions] = useState<SelectorOption[]>([]);

    // Sync controller states when extractedData becomes available (component is always mounted,
    // so useState initializes with undefined — this effect hydrates from the prop when it changes)
    useEffect(() => {
        if (extractedData) {
            setFileId(extractedData.fileId ?? 0);
            if (extractedData.contract.durationMonths != null) setDuration(extractedData.contract.durationMonths);
            if (extractedData.contract.initialDate) setInitialDate(moment(extractedData.contract.initialDate));

            // Update form fields with extracted data
            const formValues = buildInitialValues();
            formInstance.setFieldsValue(formValues);
        }
    }, [extractedData, formInstance]);

    useEffect(() => {
        fetchProperties();
        fetchPersons();
        fetchProvinces();
        const finishDate = moment(initialDate).add(duration, 'M').subtract(1, 'd');
        setFinishDate(finishDate);
    }, [duration, initialDate]);

    const onFormSubmit = (inputs: any) => {
        // Process multiple new owners
        if (inputs.newProperty?.newOwners && Array.isArray(inputs.newProperty.newOwners)) {
            inputs.newProperty.newOwners = inputs.newProperty.newOwners.map((owner: any) => ({
                ...owner,
                numberDoc: owner.numberDoc?.replace(/\./g, ''),
            }));
        }
        // Process multiple new renters
        if (inputs.newRenters && Array.isArray(inputs.newRenters)) {
            inputs.newRenters = inputs.newRenters.map((renter: any) => ({
                ...renter,
                numberDoc: renter.numberDoc?.replace(/\./g, ''),
            }));
        }
        // Process multiple new guarantors
        if (inputs.newGuarantors && Array.isArray(inputs.newGuarantors)) {
            inputs.newGuarantors = inputs.newGuarantors.map((guarantor: any) => ({
                ...guarantor,
                numberDoc: guarantor.numberDoc?.replace(/\./g, ''),
            }));
        }
        setIsLoading(true);
        const propertyId = inputs.propertyId == -1 ? null : inputs.propertyId;
        [inputs.owners];
        delete inputs.date;
        const dto = plainToClass(ContractDto, inputs);
        dto.initialDate = initialDate;
        dto.finishDate = finishDate;
        dto.durationMonths = inputs.durationMonths;
        dto.propertyId = propertyId;
        dto.percentageByAdministration = Number(inputs.percentageByAdministration);
        dto.updateFrequency = Number(inputs.updateFrequency);
        if (fileId !== 0) {
            dto.fileId = fileId;
        }

        contractsService
            .addContract(dto)
            .then((response) => {
                formInstance.resetFields();
                setFileId(0);
                setFileUrl('');
                setIsFileLoading(false);
                fetchPersons();
                fetchProperties();
                onContractAdded(response);
            })
            .catch((err) => {
                if (err.response.data.detail && err.response.data.detail.indexOf('have') !== -1) {
                    messenger.showErrorMessage({ key: 'add-contracts.new-contract-error' });
                } else {
                    messenger.showErrorMessage({ key: 'add-contracts.default-error' });
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    };
    const mapPropertiesDtoToSelectorOptions = (dto: CompletePropertyDto): SelectorOption => {
        return {
            value: dto.id ?? 0,
            label: dto.alias ?? '',
        };
    };
    const mapDtoPersonsToSelectorOptions = (dto: CompletePersonDto): SelectorOption => {
        return {
            value: dto.id ?? 0,
            label: `${dto.name} ${dto.lastName}` ?? '',
        };
    };

    const fetchProperties = () => {
        propertiesService.getAllProperties().then((dtos) => {
            setPropertiesOptions(dtos.map(mapPropertiesDtoToSelectorOptions));
        });
    };
    const fetchPersons = () => {
        personsService.getAllPersons().then((dtos) => {
            setPersonsOptions(dtos.map(mapDtoPersonsToSelectorOptions));
        });
    };
    const validateRenters = (rule: any, value: any, callback: any) => {
        if (!Array.isArray(value) || value.length > 10 || value.length <= 0) {
            callback('Permitidos: máximo diez - minimo uno');
        } else {
            callback();
        }
        return;
    };
    const validateGuarantors = (rule: any, value: any, callback: any) => {
        if (!Array.isArray(value) || value.length > 10 || value.length <= 0) {
            callback('Permitidos: máximo diez - minimo uno');
        } else {
            callback();
        }
        return;
    };
    const validateOwners = (rule: any, value: any, callback: any) => {
        if (!Array.isArray(value) || value.length > 3 || value.length <= 0) {
            callback('Permitidos: máximo 3 - mínimo uno');
        } else {
            callback();
        }
        return;
    };

    const validateProperty = (rule: any, value: any, callback: any) => {
        if (value == null || value === '') {
            callback('Debe elegir una propiedad');
        } else {
            callback();
        }
        return;
    };

    const validateUpdateFrequency = (rule: any, value: any, callback: any) => {
        if (value == null || value === '') {
            callback('Solo se puede elegir un período de actualización');
        } else {
            callback();
        }
        return;
    };
    const handleChange = (info: any) => {
        if (info.file.status === 'uploading') {
            setIsFileLoading(true);
            return;
        }
        if (info.file.status === 'done') {
            const newUrl = info.file.response;
            const newFileId = info.file.response.id;
            setFileId(newFileId);
            setFileUrl(newUrl);
            setIsFileLoading(false);
        } else {
            setFileId(0);
            setFileUrl('');
            setIsFileLoading(false);
        }
    };
    const initialDateChange = (value: any) => {
        setInitialDate(value);
    };

    const durationChange = (value: any) => {
        setDuration(value);
    };

    const onCancel = () => {
        setFileId(0);
        setFileUrl('');
        setIsFileLoading(false);
        formInstance.resetFields();
    };

    const fetchProvinces = () => {
        settingsService.getAllProvinces().then((dtos) => {
            setProvincesOptions(dtos.map(mapDtoProvincesToSelectorOptions));
        });
    };

    const mapDtoProvincesToSelectorOptions = (dto: ProvinceDto): SelectorOption => {
        return {
            value: dto.id ?? 0,
            label: dto.name ?? '',
        };
    };

    const buildInitialValues = (): any => {
        const base: any = {
            expiredDay: extractedData?.contract.expiredDay ?? 10,
            initialDayInterest: 1,
            typeOfCharge: 'percentage',
            debtCollection: 0,
        };
        if (!extractedData) return base;

        // Property
        if (extractedData.property.existingId != null) {
            base.propertyId = extractedData.property.existingId;
        } else {
            base.propertyId = -1;
            const ownerValue = extractedData.owner.existingId != null ? extractedData.owner.existingId : -1;
            base.newProperty = {
                street: extractedData.property.street ?? undefined,
                number: extractedData.property.number ?? undefined,
                floorDepartment: extractedData.property.floor ?? undefined,
                city: extractedData.property.city ?? undefined,
                district: extractedData.property.district ?? undefined,
                owners: [ownerValue],
                ...(ownerValue === -1 && extractedData.owner.name
                    ? {
                          newOwners: [
                              {
                                  name: extractedData.owner.name ?? undefined,
                                  lastName: extractedData.owner.lastName ?? undefined,
                                  numberDoc: extractedData.owner.dni ?? undefined,
                                  phone: extractedData.owner.phone ?? undefined,
                                  email: extractedData.owner.email ?? undefined,
                              },
                          ],
                      }
                    : {}),
            };
        }

        // Renters
        if (extractedData.renter.existingId != null) {
            base.rentersId = [extractedData.renter.existingId];
        } else if (extractedData.renter.name) {
            base.rentersId = [-1];
            base.newRenters = [
                {
                    name: extractedData.renter.name ?? undefined,
                    lastName: extractedData.renter.lastName ?? undefined,
                    numberDoc: extractedData.renter.dni ?? undefined,
                    phone: extractedData.renter.phone ?? undefined,
                    email: extractedData.renter.email ?? undefined,
                },
            ];
        }

        // Guarantors
        if (extractedData.guarantor) {
            if (extractedData.guarantor.existingId != null) {
                base.guarantorsId = [extractedData.guarantor.existingId];
            } else if (extractedData.guarantor.name) {
                base.guarantorsId = [-1];
                base.newGuarantors = [
                    {
                        name: extractedData.guarantor.name ?? undefined,
                        lastName: extractedData.guarantor.lastName ?? undefined,
                        numberDoc: extractedData.guarantor.dni ?? undefined,
                        phone: extractedData.guarantor.phone ?? undefined,
                        email: extractedData.guarantor.email ?? undefined,
                    },
                ];
            }
        }

        // Contract fields
        if (extractedData.contract.initialDate) {
            base.date = moment(extractedData.contract.initialDate);
        }
        if (extractedData.contract.durationMonths != null) {
            base.durationMonths = extractedData.contract.durationMonths;
        }
        if (extractedData.contract.startPrice != null) {
            base.startPrice = extractedData.contract.startPrice;
        }
        if (extractedData.contract.percentageByAdministration != null) {
            base.percentageByAdministration = extractedData.contract.percentageByAdministration;
        }
        if (extractedData.contract.updateFrequency != null) {
            base.updateFrequency = extractedData.contract.updateFrequency;
        }

        return base;
    };

    const initialValuesInformationInterest = buildInitialValues();

    const onClickSelect = () => {
        setSelectIsOpen(!selectIsOpen);
    };

    const onBlurSelect = () => {
        setSelectIsOpen(false);
    };

    return {
        form: formInstance,
        isLoading,
        propertiesOptions: propertiesOptions,
        personsOptions: personsOptions,
        isFileLoading,
        fileUrl,
        fileId,
        handleChange,
        onFormSubmit,
        validateRenters: validateRenters,
        validateGuarantors: validateGuarantors,
        validateUpdateFrequency: validateUpdateFrequency,
        validateProperty,
        onCancel,
        durationChange,
        duration,
        validateOwners,
        initialDateChange,
        initialDate,
        finishDate,
        provincesOptions,
        initialValues: initialValuesInformationInterest,
        onClickSelect,
        selectIsOpen,
        onBlurSelect,
    };
};
